import React, { useState } from "react";
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Icon,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { ExternalLinkIcon, WarningTwoIcon } from "@chakra-ui/icons";
import { FaFilePdf } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { normalizeSiteLanguage } from "../i18n/language";

interface PdfViewerProps {
  title: string;
  zhUrl: string;
  enUrl: string;
  /** Simplified Chinese PDF; falls back to zhUrl when omitted. */
  zhCNUrl?: string;
  contentKey?: string;
}

const PdfViewer: React.FC<PdfViewerProps> = ({
  title,
  zhUrl,
  enUrl,
  zhCNUrl,
  contentKey,
}) => {
  const { i18n, t } = useTranslation();
  const siteLanguage = normalizeSiteLanguage(
    i18n.resolvedLanguage || i18n.language,
  );
  const pdfUrl =
    siteLanguage === "en"
      ? enUrl
      : siteLanguage === "zh-CN"
        ? zhCNUrl || zhUrl
        : zhUrl;

  const rawContent = contentKey ? t(contentKey) : undefined;
  const hasHtmlContent =
    !!rawContent && rawContent !== contentKey && rawContent.includes("<");

  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  if (hasHtmlContent) {
    return (
      <Box>
        {/* Meta bar */}
        <Flex
          align="center"
          justify="space-between"
          mb={6}
          flexWrap="wrap"
          gap={3}
        >
          <HStack spacing={2} flexWrap="wrap">
            <Icon as={FaFilePdf as React.ElementType} color="red.400" boxSize={4} />
            <Badge
              colorScheme="gray"
              borderRadius="md"
              px={2}
              py={0.5}
              fontSize="xs"
              variant="subtle"
            >
              {t("pdfViewer.languageBadge")}
            </Badge>
          </HStack>
          <Button
            as="a"
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            size="sm"
            variant="outline"
            colorScheme="gray"
            rightIcon={<ExternalLinkIcon />}
            fontSize="xs"
            borderRadius="lg"
          >
            {t("pdfViewer.downloadPdf")}
          </Button>
        </Flex>

        <Divider mb={8} borderColor="gray.200" />

        {/* HTML content */}
        <Box
          sx={{
            h2: {
              fontSize: ["lg", "xl"],
              fontWeight: "bold",
              color: "#23272F",
              mt: 10,
              mb: 4,
              pb: 2,
              borderBottom: "2px solid #FFE8E9",
            },
            h3: {
              fontSize: ["md", "lg"],
              fontWeight: "semibold",
              color: "#333",
              mt: 6,
              mb: 3,
            },
            p: {
              color: "#505663",
              lineHeight: 1.9,
              mb: 4,
              fontSize: ["sm", "md"],
            },
            ul: { pl: 6, mb: 4 },
            ol: { pl: 6, mb: 4 },
            li: {
              color: "#505663",
              lineHeight: 1.8,
              mb: 1.5,
              fontSize: ["sm", "md"],
            },
            strong: { color: "#23272F", fontWeight: "600" },
            a: {
              color: "#3182CE",
              textDecoration: "underline",
              _hover: { color: "#2B6CB0" },
              wordBreak: "break-all",
            },
          }}
          dangerouslySetInnerHTML={{ __html: rawContent! }}
        />

        <Divider mt={10} mb={6} borderColor="gray.200" />

        {/* Footer download link */}
        <Flex justify="flex-end">
          <Button
            as="a"
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            size="sm"
            variant="ghost"
            colorScheme="gray"
            rightIcon={<ExternalLinkIcon />}
            fontSize="xs"
          >
            {t("pdfViewer.downloadFullPdf")}
          </Button>
        </Flex>
      </Box>
    );
  }

  /* ── PDF iframe fallback (when no HTML content available) ── */
  return (
    <Box>
      <Box
        position="relative"
        borderRadius="xl"
        overflow="hidden"
        minH="80vh"
        bg="gray.50"
      >
        {isLoading && !hasError && (
          <Flex
            position="absolute"
            inset={0}
            align="center"
            justify="center"
            bg="gray.50"
            zIndex={1}
            flexDirection="column"
            gap={3}
          >
            <Spinner size="xl" color="red.400" thickness="3px" speed="0.8s" />
            <Text fontSize="sm" color="gray.400" fontWeight="medium">
              {t("pdfViewer.loading")}
            </Text>
          </Flex>
        )}
        {hasError ? (
          <Flex
            align="center"
            justify="center"
            minH="70vh"
            flexDirection="column"
            gap={4}
            px={6}
          >
            <WarningTwoIcon boxSize={12} color="gray.300" />
            <Text color="gray.500" fontWeight="semibold" textAlign="center">
              {t("pdfViewer.loadError")}
            </Text>
            <Button
              as="a"
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              colorScheme="red"
              rightIcon={<ExternalLinkIcon />}
            >
              {t("pdfViewer.openDocument")}
            </Button>
          </Flex>
        ) : (
          <iframe
            src={pdfUrl}
            title={title}
            width="100%"
            style={{ display: "block", border: "none", height: "80vh" }}
            onLoad={() => setIsLoading(false)}
            onError={() => {
              setIsLoading(false);
              setHasError(true);
            }}
          />
        )}
      </Box>
      <Text fontSize="xs" color="gray.400" textAlign="center" mt={3}>
        {t("pdfViewer.fallbackPrefix")}
        <Box
          as="a"
          href={pdfUrl}
          target="_blank"
          rel="noopener noreferrer"
          color="blue.400"
          textDecoration="underline"
          _hover={{ color: "blue.600" }}
        >
          {t("pdfViewer.fallbackLink")}
        </Box>
        {t("pdfViewer.fallbackSuffix")}
      </Text>
    </Box>
  );
};

export default PdfViewer;
